import { createFileRoute, Link } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { z } from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { useAuth } from '../../features/auth/auth-context';
import {
  MY_SETTINGS_GROUPS,
  MY_SETTINGS_SECTIONS,
  type MySettingsSection,
  mySettingsSectionById,
} from '../../features/settings/my-settings-sections';
import {
  useMarkTelegramSetupSeen,
  useTelegramSetupReminder,
} from '../../features/telegram/setup-reminder';
import { useShellMode } from '../../lib/shell-mode';

/** The nav layout section — meaningless in the limited shell's fixed bar. */
const LIMITED_SHELL_HIDDEN_SECTION = 'nav';

/**
 * Cài đặt của **chính người đang đăng nhập** — APM-805, dựng lại thành hub
 * theo mẫu Cấu hình dự án ngày 2026-09-26.
 *
 * ## Vì sao có trang này thay vì nhét vào console quản trị
 *
 * Key AI thuộc về từng người: ai nhập key thì người đó dùng được AI. Đặt nó ở
 * `/admin` (sau quyền `user:manage`) nghĩa là chỉ quản trị viên bật được AI cho
 * mình, còn cả đội thì không — ngược hẳn với mô hình.
 *
 * Trang này **không** đòi quyền gì: mọi người đăng nhập đều vào được, và mọi
 * thứ ở đây chỉ nói về dữ liệu của chính họ.
 *
 * ## Mục đang mở nằm ở URL
 *
 * Cùng lý do với `ProjectSettingsPanel`: nút Back, link dán cho người khác và
 * F5 đều đúng. Và `?section=telegram` vốn đã là đích của nút "Mở trình duyệt để
 * kết nối" trong Mini App (APM-TG-n) — query chứ không phải `#telegram`, vì
 * người chưa đăng nhập đi vòng qua `/login?next=…` và `safeNext` bỏ phần `#`.
 *
 * ## Vì sao trang chi tiết CÓ `CardHeader`, khác bản dự án
 *
 * Các panel ở đây không tự vẽ tiêu đề — trang cũ luôn bọc chúng trong một
 * `CardHeader`. Bỏ đi thì panel mở ra không có tên.
 */
const SettingsSearch = z.object({
  section: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/settings')({
  validateSearch: SettingsSearch,
  component: SettingsPage,
});

function SettingsPage(): ReactNode {
  const { t } = useTranslation();
  const { section: sectionId } = Route.useSearch();
  const { user } = useAuth();
  /*
    Badge "Cần thiết lập" — người dùng yêu cầu 2026-09-25: chấm đỏ trên avatar
    dẫn tới trang này, và tới nơi phải thấy **bấm vào đâu** để nó tắt. Nó hiện
    trên thẻ Telegram ở hub và trên tiêu đề mục Telegram; bấm vào trong mục thì
    tắt cả hai (APM-TG-p).
  */
  const telegramReminder = useTelegramSetupReminder(user?.id ?? 'anonymous');
  const markTelegramSeen = useMarkTelegramSetupSeen(user?.id ?? 'anonymous');

  /*
    Limited shell (APM-PSPACE-g): its bar is fixed and ignores the personal nav layout,
    so the "Thanh điều hướng" section would arrange items that are never shown.
  */
  const limited = useShellMode() === 'limited';
  const sections = limited
    ? MY_SETTINGS_SECTIONS.filter((item) => item.id !== LIMITED_SHELL_HIDDEN_SECTION)
    : MY_SETTINGS_SECTIONS;

  const groups = MY_SETTINGS_GROUPS.filter((group) =>
    sections.some((item) => item.group === group),
  );

  const section = sections.find((item) => item.id === mySettingsSectionById(sectionId)?.id);
  if (section) {
    return (
      <SettingsSectionPage
        section={section}
        reminder={section.id === 'telegram' && telegramReminder}
        onClickCapture={section.id === 'telegram' ? markTelegramSeen : undefined}
      />
    );
  }

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <h1 className="text-2xl font-semibold tracking-tight" data-tour="settings-page">
        {t('nav.settings')}
      </h1>

      {groups.map((group) => (
        <section key={group} className="flex flex-col gap-3">
          <h2 className="text-sm font-medium text-muted-foreground">
            {t(`mySettings.nav.group.${group}`)}
          </h2>

          <div className="grid gap-3 sm:grid-cols-2">
            {sections
              .filter((item) => item.group === group)
              .map((item) => (
                <SectionCard
                  key={item.id}
                  section={item}
                  reminder={item.id === 'telegram' && telegramReminder}
                />
              ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ReminderBadge(): ReactNode {
  const { t } = useTranslation();
  return (
    <span
      data-testid="telegram-card-reminder"
      className="rounded-full bg-rag-red px-1.5 text-[10px] font-medium leading-4 text-white"
    >
      {t('telegramLink.reminder.cardBadge')}
    </span>
  );
}

/** Thẻ là một `Link` — cùng lý do với `SectionCard` của Cấu hình dự án. */
function SectionCard({
  section,
  reminder,
}: {
  readonly section: MySettingsSection;
  readonly reminder: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const Icon = section.icon;

  return (
    <Link
      from="/settings"
      to="/settings"
      search={{ section: section.id }}
      data-tour={section.tourTarget}
      className="rounded-DEFAULT border border-border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      <span className="flex items-start gap-3">
        <Icon aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex items-center gap-2 text-sm font-medium">
            {t(section.titleKey)}
            {reminder ? <ReminderBadge /> : null}
          </span>
          <span className="text-xs text-muted-foreground">{t(section.descriptionKey)}</span>
        </span>
        <ChevronRight aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      </span>
    </Link>
  );
}

function SettingsSectionPage({
  section,
  reminder,
  onClickCapture,
}: {
  readonly section: MySettingsSection;
  readonly reminder: boolean;
  readonly onClickCapture: (() => void) | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const Icon = section.icon;

  return (
    <div className="flex max-w-4xl flex-col gap-4">
      <Link
        from="/settings"
        to="/settings"
        search={{ section: undefined }}
        className="inline-flex items-center gap-1.5 self-start text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft aria-hidden className="size-4" />
        {t('mySettings.nav.back')}
      </Link>

      <Card onClickCapture={onClickCapture}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon aria-hidden className="size-5 text-muted-foreground" />
            {t(section.titleKey)}
            {reminder ? <ReminderBadge /> : null}
          </CardTitle>
          <CardDescription>{t(section.descriptionKey)}</CardDescription>
        </CardHeader>
        <CardContent>{section.render()}</CardContent>
      </Card>
    </div>
  );
}
